import { DataSource, DataSourceCredentials, DataSourceType } from "@coeff/api";
import { COLORS } from "@coeff/utils";
import { useQuery } from "@tanstack/react-query";
import { Layout } from "antd";
import React, { ReactNode, useEffect, useState } from "react";
import styled from "styled-components";

import { Button } from "../../../common/Button";
import { ConfirmBox } from "../../../common/ConfirmBox";
import { DotsLoader } from "../../../common/DotsLoader";
import { ErrorMessage } from "../../../common/ErrorMessage";
import { Graphics } from "../../../common/Graphics";
import { ConnectPlugIcon, Tableau } from "../../../common/Icons";
import { Input } from "../../../common/Input";
import { SecurityInfo } from "../../../common/SecurityInfo";
import { useApiClient, useEventManagerContext, useMessage } from "../../../globals/contexts";
import {
  QueryCacheKey,
  useAuthContext,
  WithPostDataSourceCreationProps,
} from "../../context/AuthContext";
import {
  extractTableauSiteNameFromURL,
  getDataSourceApiErrorMessage,
  getReauthErrorMessageProps,
  isAxiosReauthError,
  isTableauCheckConnectionResponse,
  sentryCapture,
} from "../../utils";

const { Content, Footer } = Layout;

const StyledContent = styled(Content)`
  && {
    height: 100%;
    background-color: ${COLORS.white};
    padding: 14px 0px;

    p {
      margin-top: 0px;
    }

    .datasourceLogo {
      margin: auto;
      display: block;
    }

    .graphicsLogo {
      margin-top: 1rem;

      .datasourceLogo {
        width: 60px;
        height: 60px;
      }
    }

    .contactLink {
      color: inherit;
      text-decoration: underline;
      text-decoration-style: dashed;
      text-underline-offset: 3px;
    }
  }
`;

const StyledFooter = styled(Footer)`
  position: sticky;
  bottom: 0px;
  padding: 0px;
  background: white;
  z-index: 50;
`;

const StyledConfirmBox = styled(ConfirmBox)`
  .ant-modal-content {
    .ant-modal-body {
      padding: 8px 24px;
    }

    .ant-modal-footer {
      padding: 8px 24px 16px;

      .submit {
        width: 100%;
      }
    }
  }
`;

const StyledLabel = styled.label`
  display: block;
  width: auto;
  margin: 15px;
  color: ${COLORS.black85};
  font-weight: 500;

  .coeff__input {
    height: 32px;
    margin: 7px auto;
    border-color: ${COLORS.black15};
  }
`;

const InnerPanel = styled.div`
  background: ${COLORS.coeblue};
  border-radius: 5px;
  padding: 10px;
  margin: 15px;

  .title {
    text-align: center;
    color: ${COLORS.black85};
    font-weight: 500;
  }

  .description {
    text-align: center;
    color: ${COLORS.black45};
    font-weight: 600;
    font-size: 12px;
    line-height: 16px;

    a {
      display: block;
    }
  }

  label {
    display: flex;
    width: auto;
    color: ${COLORS.black85};
    font-weight: 500;
    align-items: center;
    margin: 10px 0px;

    .labelText {
      width: 80px;
    }

    .coeff__input {
      height: 32px;
      margin: auto;
      border-color: ${COLORS.black15};
    }
  }
`;

const ButtonSection = styled.section<{ noBoxShadow?: boolean }>`
  width: 100%;
  display: flex;
  position: sticky;
  bottom: 0px;
  padding: 20px 5px 10px;
  background: white;
  justify-content: center;
  z-index: 25;
  ${({ noBoxShadow }) => {
    if (noBoxShadow) {
      return "";
    }

    return "box-shadow: 0px -10px 10px -10px #dddddd;";
  }}

  .ant-btn {
    flex: 1;
    margin: 0px 10px;
    max-width: 200px;

    svg {
      width: 1rem;
      margin-right: 5px;
      position: relative;
      top: 2px;
    }
  }
`;

const HelpText = styled.div<{ isError?: boolean }>`
  font-size: 12px;
  line-height: 16px;
  color: ${({ isError }) => {
    if (isError) {
      return COLORS.red;
    }

    return COLORS.black45;
  }};
  font-weight: 600;
  padding: 2px 4px;
`;

type UiErrors = {
  url?: boolean;
  username?: boolean;
  password?: boolean;
  tokenName?: boolean;
  tokenSecret?: boolean;
};

type Props = WithPostDataSourceCreationProps & {
  // When rendered inside the PPress auth form, the host (AuthorizePPress) supplies
  // the page header / help panel, so we hide our own chrome and footer security info.
  isPPressComponent?: boolean;
  customDescription?: ReactNode;
  dataSource?: DataSource;
  dataSourceId?: string;
  event_props: {
    email?: string;
    domain?: string;
    user_id?: string;
  };
  onDataSourceUpserted: (dataSource: DataSource) => void;
  onCancel: () => void;
  // Lets the host render its own connection summary (e.g. addon DataSourceItem) in edit mode.
  renderDataSourceItem?: (args: { dataSource?: DataSource }) => React.ReactNode;
};

const AuthorizeTableauContentBase: React.FC<Props> = ({
  initPostDataSourceCreation,
  customDescription = "",
  isPPressComponent,
  dataSource: initialDataSource,
  dataSourceId,
  event_props,
  onDataSourceUpserted,
  onCancel,
  renderDataSourceItem,
}) => {
  const message = useMessage();
  const api = useApiClient();
  const { track } = useEventManagerContext();
  const { analyticsContext } = useAuthContext();

  const fromConnectedSource = analyticsContext?.fromConnectedSource;
  const fromOnboarding = analyticsContext?.fromOnboarding;

  const { data: dataSource } = useQuery(
    [QueryCacheKey.DATA_SOURCE, initialDataSource?.data_source_id ?? dataSourceId],
    () =>
      api
        .getDataSource({
          data_source_id: (initialDataSource?.data_source_id ?? dataSourceId)!,
        })
        .then(response => response.data.data_source),
    {
      enabled: Boolean(initialDataSource?.data_source_id ?? dataSourceId),
      initialData: initialDataSource,
      onSuccess: dataSource => {
        setIsShared(dataSource?.is_shared_with_domain ?? false);
      },
      onError: error => {
        console.error("Error fetching data source", error);

        sentryCapture({ error });
      },
    }
  );

  const [url, setURL] = useState<string>(dataSource?.credentials?.url || "");

  const [sitename, setSitename] = useState<string>(
    dataSource?.credentials?.tableau_site_name || ""
  );

  const [tokenName, setTokenName] = useState<string>(dataSource?.credentials?.client_id || "");

  const [tokenSecret, setTokenSecret] = useState<string>(
    dataSource?.credentials?.client_secret || ""
  );

  const [isShared, setIsShared] = useState<boolean>(dataSource?.is_shared_with_domain || false);

  const [loading, setLoading] = useState<boolean>(false);

  const [connectionError, setConnectionError] = useState<unknown>();

  const [uiErrors, setUiErrors] = useState<UiErrors>({});

  const [showAuthenticationHelp, setShowAuthenticationHelp] = useState<boolean>(false);

  const [showAccessTokenHelp, setShowAccessTokenHelp] = useState<boolean>(false);

  const isEditConnectionId = dataSource?.data_source_id || "";

  const readOnly = dataSource?.user ? dataSource.user.email !== event_props.email : false;

  useEffect(() => {
    !isEditConnectionId &&
      track("conn_create_viewed", {
        import_data_source: "tableau",
        event_from: fromConnectedSource
          ? "add_connection"
          : fromOnboarding
          ? "app_onboarding"
          : "add_import",
        data_source_id: isEditConnectionId,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateConnectionDetails = () => {
    setUiErrors({});
    const newUiErrors: UiErrors = {};

    if (!url || url.length <= 0) {
      newUiErrors.url = true;
    }

    if (!tokenName) {
      newUiErrors.tokenName = true;
    }

    if (!tokenSecret) {
      newUiErrors.tokenSecret = true;
    }

    const isValid = Object.keys(newUiErrors).length <= 0;
    setUiErrors(newUiErrors);

    return isValid;
  };

  const connectTableau = async () => {
    setConnectionError(undefined);

    if (!validateConnectionDetails()) {
      return;
    }

    track(isEditConnectionId ? "conn_edit_started" : "conn_create_started", {
      import_data_source: "tableau",
      conn_data_source: "tableau",
      edited_sharing: isEditConnectionId
        ? isShared !== dataSource?.is_shared_with_domain
        : undefined,
      data_source_id: isEditConnectionId,
    });

    setLoading(true);

    try {
      const credentials: DataSourceCredentials = {
        url,
        tableau_site_name: sitename,
        client_id: tokenName,
        client_secret: tokenSecret,
      };

      const { data: checkResp } = await api.tableauCheckConnection({
        tableau_check_connection_request: {
          credentials,
        },
      });

      if (checkResp.valid) {
        setURL(checkResp.base_url!);

        const dataSourceBody: DataSource = {
          ...dataSource,
          data_source_type: "tableau",
          data_source_name: dataSource?.data_source_name || "Tableau",
          credentials: { ...credentials, url: checkResp.base_url },
          is_shared_with_domain: isShared,
        };

        const {
          data: { data_source },
        } = await (isEditConnectionId
          ? api.updateDataSource({
              data_source_id: isEditConnectionId,
              update_data_source_request: {
                data_source: dataSourceBody,
              },
            })
          : api.createDataSource({ create_data_source_request: { data_source: dataSourceBody } }));

        track(isEditConnectionId ? "conn_edit_done" : "conn_create_done", {
          import_data_source: "tableau",
          edited_sharing: isEditConnectionId
            ? isShared !== dataSource?.is_shared_with_domain
            : undefined,
          data_source_id: isEditConnectionId,
        });

        if (isEditConnectionId) {
          message.success("Connection saved");
          onDataSourceUpserted(data_source);
        } else {
          initPostDataSourceCreation({
            dataSource: data_source,
            onComplete: dataSource => onDataSourceUpserted(dataSource),
          });
        }
      } else {
        track(isEditConnectionId ? "conn_edit_failed" : "conn_create_failed", {
          import_data_source: "tableau",
          data_source_id: isEditConnectionId,
          reason: checkResp.error,
        });

        setConnectionError(checkResp);
      }
    } catch (error: unknown) {
      sentryCapture({ error });

      setConnectionError(error);
    }

    setLoading(false);
  };

  const tryToSetSiteName = () => {
    const guessedSiteName = extractTableauSiteNameFromURL(url);
    if (guessedSiteName) {
      setSitename(guessedSiteName);
    }
  };

  return (
    <Layout>
      <StyledContent>
        <>
          {!isEditConnectionId && (
            <Graphics
              graphics={
                <div className="graphicsLogo">
                  <ConnectPlugIcon />
                  <Tableau className="datasourceLogo" />
                </div>
              }
              title={`Let's Connect to Tableau`}
              description={customDescription}
            />
          )}

          {isEditConnectionId && renderDataSourceItem?.({ dataSource })}

          {isTableauCheckConnectionResponse(connectionError) ? (
            <ErrorMessage
              screen="authorizeTableau"
              title={connectionError.title ?? "Unable to connect to Tableau"}
              message={
                connectionError.error ??
                "Please check the connection information below and try again."
              }
              secondaryMessage={connectionError.source_error}
              dataSourceType={DataSourceType.Tableau}
            />
          ) : isAxiosReauthError(connectionError) ? (
            <ErrorMessage
              screen="authorizeTableau"
              {...getReauthErrorMessageProps({
                dataImport: undefined,
                dataSource,
                userId: event_props.user_id,
                action: undefined,
                importStatusOrReauthError: connectionError.response.data,
              })}
              dataSourceType={DataSourceType.Tableau}
            />
          ) : connectionError ? (
            <ErrorMessage
              screen="authorizeTableau"
              title="Unable to connect to Tableau"
              message={
                getDataSourceApiErrorMessage(connectionError) ??
                "Please check the connection information below and try again."
              }
              dataSourceType={DataSourceType.Tableau}
            />
          ) : null}

          <StyledLabel>
            Instance URL
            <Input
              size="middle"
              onChange={e => setURL(e.currentTarget.value)}
              value={url}
              disabled={readOnly}
              error={uiErrors.url}
              placeholder="https://"
              onBlur={tryToSetSiteName}
            />
            <HelpText>
              Enter the URL of your Tableau homescreen (or any worksheet) once you're logged in
            </HelpText>
          </StyledLabel>

          <StyledLabel>
            Site Name
            <Input
              size="middle"
              onChange={e => setSitename(e.currentTarget.value)}
              value={sitename}
              disabled={readOnly}
              placeholder="Site Name"
            />
            <HelpText>
              If we can't detect this from your instance URL you'll need to enter your site name.
            </HelpText>
          </StyledLabel>

          <InnerPanel>
            <div>
              <div className="description">
                Please use your Personal Access Token to link your Tableau account{" "}
                <a style={{ marginTop: 4 }} onClick={() => setShowAccessTokenHelp(true)}>
                  How to create a Personal Access Token
                </a>
              </div>
              <label>
                <span className="labelText">Name</span>
                <Input
                  size="middle"
                  onChange={e => setTokenName(e.currentTarget.value)}
                  value={tokenName}
                  disabled={readOnly}
                  error={uiErrors.tokenName}
                  placeholder="Name"
                />
              </label>

              <label>
                <span className="labelText">Secret</span>
                <Input
                  type="password"
                  size="middle"
                  onChange={e => setTokenSecret(e.currentTarget.value)}
                  value={tokenSecret}
                  disabled={readOnly}
                  error={uiErrors.tokenSecret}
                  placeholder="Secret"
                />
              </label>
            </div>
          </InnerPanel>
        </>
      </StyledContent>

      <StyledFooter>
        {!readOnly && (
          <ButtonSection>
            <Button type="default" onClick={onCancel}>
              Cancel
            </Button>

            <Button disabled={loading} type="primary" onClick={connectTableau}>
              {loading ? (
                <DotsLoader style={{ margin: "auto" }} />
              ) : isEditConnectionId ? (
                "Save"
              ) : (
                "Connect"
              )}
            </Button>
          </ButtonSection>
        )}

        {!isEditConnectionId && !isPPressComponent && <SecurityInfo />}
      </StyledFooter>

      <ConfirmBox
        cancelText={"Got it"}
        title={`Authentication Methods`}
        visible={showAuthenticationHelp}
        onCancel={() => {
          setShowAuthenticationHelp(false);
        }}
      >
        <p>
          <strong>Username/Password:</strong> If you have a direct login to Tableau instead of using
          a common SSO credential (e.g. Okta)
        </p>
        <p>
          <strong>Personal Access Token:</strong> If you use SSO (e.g. Okta) to access Tableau, then
          create and provide a token from your Tableau Account Settings page.
        </p>
      </ConfirmBox>

      <StyledConfirmBox
        showSubmit={true}
        submitText={"Got it"}
        title={`How to find your Personal Access Token`}
        visible={showAccessTokenHelp}
        onSubmit={() => {
          setShowAccessTokenHelp(false);
        }}
      >
        <p>Your Account Settings page in Tableau lets you manage your Personal Access Tokens.</p>
        <p>
          Create a new token by entering a name for your token (e.g. Coefficient) and then copy the
          generated token.{" "}
          <a
            href="https://help.tableau.com/current/pro/desktop/en-us/useracct.htm#create-and-revoke-personal-access-tokens"
            target="_blank"
          >
            Learn More
          </a>
        </p>
      </StyledConfirmBox>
    </Layout>
  );
};

export const AuthorizeTableauContent = AuthorizeTableauContentBase;
